import { PhoneValidation } from 'pos-shared/utils/validationHelpers.ts';
import { z } from 'zod';

import { DayJsSchema } from './commonSchema.ts';
import { PagedRequestSchema, PagedResultSchema } from './pagingSchema.ts';
import { ServiceTypesSchema } from './staticConfigSchema.ts';

const GEOLOCATION_LIMITS = {
  LATITUDE: {
    MIN: -90,
    MAX: 90,
  },
  LONGITUDE: {
    MIN: -180,
    MAX: 180,
  },
};

// shared
export const LocationLinkedAsset = z.object({
  id: z.string(),
  name: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  description: z.string().optional(),
  typeId: z.string(),
  assetTypeName: z.string().optional(),
  reference: z.string(),
  referenceType: z.string().optional(),
  lastModified: z.coerce.date<Date>().optional(),
  capabilities: z.array(z.any()),
  skills: z.array(z.any()),
  isActive: z.boolean().optional(),
  defaultDays: z.array(z.string()).optional(),
});

export const LocationLinkedAssetResultSchema = z.object({
  serviceLocationId: z.uuid().optional(),
  linkedAssets: z.array(LocationLinkedAsset),
});

export const LocationLinkedAssetResponseSchema = PagedResultSchema.extend({
  results: z.array(LocationLinkedAssetResultSchema),
});

export const ContactSchema = z.object({
  emailAddress: z.string().nullable().optional(),
  telephone: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
});

export const CreationSchema = z.object({
  openedDate: z.string().nullable().optional(),
  closedDate: z.string().nullable().optional(),
  relocatedDate: z.string().nullable().optional(),
});

export const AssignedUserIdListSchema = z.array(
  z.string().nullable().optional()
);

export const DateSpecificSchema = z.object({
  from: z.string(),
  to: z.string(),
});

export const AvailabilitiesTimesSchema = z.object({
  day: z.string().nullable().optional(),
  am: z.boolean(),
  pm: z.boolean(),
  dateSpecific: DateSpecificSchema,
});

export const AvailabilitiesTimesListSchema = z.array(AvailabilitiesTimesSchema);

export const AvailabilitiesSchema = z.object({
  availabilityId: z.string().nullable().optional(),
  day: z.string().nullable().optional(),
  times: AvailabilitiesTimesListSchema.nullable().optional(),
  dateSpecific: DateSpecificSchema,
});

export const AvailabilitiesListSchema = z.array(AvailabilitiesSchema);

export const LocationCustomConfigurationSchema = z.object({
  id: z.string(),
  value: z.string(),
});

export const LocationCustomConfigurationListSchema = z.array(
  LocationCustomConfigurationSchema
);

export const LocationManifestFormSchema = z.object({
  locationId: z.uuid(),
  configId: z.uuid().optional(),
  configName: z.string(),
  value: z.string(),
  isDefault: z.boolean(),
});

export type LocationManifestFormModel = z.infer<
  typeof LocationManifestFormSchema
>;

export const LocationManifestUpdateFormSchema = LocationManifestFormSchema.omit(
  {
    value: true,
  }
).extend({ value: DayJsSchema });

export type LocationManifestUpdateFormModel = z.infer<
  typeof LocationManifestUpdateFormSchema
>;

// fe
export const AddressBeSchema = z.object({
  text: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  addressLine3: z.string().nullable().optional(),
  addressLine4: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  countryCode: z.string().nullable().optional(),
  timeZone: z.string().nullable().optional(),
  geoLocation: z
    .object({
      latitude: z.number().nullable().optional(),
      longitude: z.number().nullable().optional(),
    })
    .optional(),
});

export const LocationCategorySchema = z.object({
  id: z.uuid(),
  lastModified: z.coerce.date<Date>().optional(),
  name: z.string().nonempty().max(50),
  description: z.string().nonempty().max(100),
});

export const CreateLocationCategorySchema = LocationCategorySchema.pick({
  name: true,
  description: true,
});

export const LocationCategoryRequestSchema = z
  .object({
    listOfIds: z.array(z.uuid()).optional(),
  })
  .extend(PagedRequestSchema.shape);

export const LocationCategoryResponseSchema = z
  .object({
    results: z.array(LocationCategorySchema),
  })
  .extend(PagedResultSchema.shape);

export const LocationCategoryLocationsSchema = z
  .object({
    // todo - this will expand
  })
  .extend(PagedResultSchema.shape);

export const LocationSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  code: z.string().nullable().optional(),
  contact: ContactSchema.optional(),
  creation: CreationSchema.optional(),
  address: AddressBeSchema.optional(),
  openingHoursGroupId: z.string().nullable().optional(),
  businessOrganisationId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  assignedUsers: AssignedUserIdListSchema.optional(),
  customConfigurations: LocationCustomConfigurationListSchema.optional(),
  isActive: z.boolean().optional(),
  lastModified: z.string().nullable().optional(),
  defaultRadiusInMeters: z.number().nullable().optional(),
  serviceTypes: ServiceTypesSchema.array(),
  // Only one category should be linked at the moment.
  categoryId: z.uuid().nullable().optional(),
  linkedCategories: z.array(LocationCategorySchema).optional(),
});

export const LocationListSchema = z.array(LocationSchema);

export const LocationSearchResultsSchema = z
  .object({
    results: LocationListSchema,
  })
  .extend(PagedResultSchema.shape);

export const ContactCreateEditSchema = z.object({
  emailAddress: z
    .email()
    .min(1, { error: 'Email is required' })
    .max(100, 'Email cannot exceed 100 characters')
    .nullable()
    .optional(),
  telephone: z
    .string()
    .min(1, { error: 'Phone Number is required' })
    .regex(new RegExp(/^[0-9+][0-9]*$/), 'Invalid Phone Number')
    .nullable()
    .optional(),
  fax: z
    .string()
    .max(13, 'Fax Number cannot exceed 13 characters')
    .refine((value) => {
      if (value === '' || value === null) {
        return true;
      }
      return PhoneValidation.test(value);
    }, 'Invalid fax number')
    .optional(),
});

export const GeolocationCreateEditSchema = z.object({
  latitude: z
    .number({
      error: 'Latitude is required and must be a number',
    })
    .min(GEOLOCATION_LIMITS.LATITUDE.MIN)
    .max(GEOLOCATION_LIMITS.LATITUDE.MAX),
  longitude: z
    .number({
      error: 'Longitude is required and must be a number',
    })
    .min(GEOLOCATION_LIMITS.LONGITUDE.MIN)
    .max(GEOLOCATION_LIMITS.LONGITUDE.MAX),
});

export const AddressCreateEditSchema = z.object({
  addressLine1: z
    .string()
    .min(1, { error: 'Address Line 1 is required' })
    .max(50, 'Address Line 1 cannot exceed 50 characters')
    .nullish(),
  addressLine2: z
    .string()
    .max(50, 'Address Line 2 cannot exceed 50 characters')
    .optional()
    .nullish(),
  addressLine3: z
    .string()
    .max(50, 'Address Line 3 cannot exceed 50 characters')
    .optional()
    .nullish(),
  addressLine4: z
    .string()
    .min(1, { error: 'Address Line 4 is required' })
    .max(50, 'Address Line 4 cannot exceed 50 characters')
    .nullish(),
  postalCode: z
    .string()
    .min(2, 'ZIP / Postcode must be at least 2 characters')
    .max(10, 'ZIP / Postcode cannot exceed 10 characters')
    .nonempty('ZIP / Postcode is required')
    .nullish(),
  state: z.string().max(50, 'State cannot exceed 50 characters').nullish(),
  country: z.string().min(2).nonempty().nullish(),
  geoLocation: GeolocationCreateEditSchema,
  timeZone: z.string(),
});

const ServiceLocationConfigurationSchema = z.object({
  key: z.string(),
  value: z.string(),
});

export const ServiceLocationConfigurationUpdateRequestSchema = z.object({
  id: z.uuid(),
  configurations: ServiceLocationConfigurationSchema.array(),
});

export const ServiceLocationConfigurationV2UpdateRequestSchema = z.object({
  id: z.uuid(),
  value: z.string(),
});

export const ServiceLocationConfigurationV2CreateRequestSchema = z.object({
  serviceLocationId: z.uuid(),
  name: z.string(),
  value: z.string(),
  globalSettingId: z.uuid().optional(),
});

export const ServiceLocationConfigurationGetResponseSchema =
  ServiceLocationConfigurationSchema.array();

export const ServiceLocationConfigurationSchemaV2 = z.object({
  key: z.number().optional(),
  id: z.uuid(),
  name: z.string(),
  value: z.string(),
  serviceLocationId: z.uuid(),
  createdBy: z.string(),
  dateCreated: z.coerce.date<Date>(),
  dateUpdated: z.coerce.date<Date>(),
  updatedBy: z.string(),
  description: z.string().optional(),
  globalSettingId: z.uuid().optional(),
});

export const LocationIntegrationFindFERequestSchema = z.object({
  id: z.uuid().nullish().optional(),
  name: z.string().nullish().optional(),
  integrationName: z.string().nullish().optional(),
});

export const ServiceLocationConfigurationV2GetRequestSchema = z
  .object({
    listOfIds: z.array(z.uuid()).optional(),
    listOfNames: z.array(z.string()).optional(),
    listOfServiceLocationIds: z.array(z.uuid()).optional(),
  })
  .extend(PagedRequestSchema.shape);

export const ServiceLocationConfigurationV2GetResponseSchema =
  PagedResultSchema.extend({
    results: z.array(ServiceLocationConfigurationSchemaV2),
  });

export const ServiceLocationConfigurationV2DeleteRequestSchema = z.object({
  configurationId: z.string(),
});

export const LocationEpicorLoginSchema = z.object({
  username: z.string().nullish(),
  password: z.string().nullish(),
});

export const LocationCreateEditSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(1, { error: 'Service Location Name is required' })
    .max(50, 'Service Location Name cannot exceed 50 characters')
    .nullable(),
  contact: ContactCreateEditSchema.optional(),
  address: AddressCreateEditSchema,
  code: z
    .string()
    .min(1, { error: 'Service Location Code is required' })
    .max(25, 'Service Location Code cannot exceed 25 characters')
    .regex(/^[A-Za-z0-9]+$/, 'No non-alphanumeric characters or spaces'),
  serviceTypes: ServiceTypesSchema.array().nonempty(),
  configurations: ServiceLocationConfigurationSchema.array().optional(),
  epicorLogin: LocationEpicorLoginSchema.optional(),
  isActive: z.boolean(),
  categoryId: z.uuid().nullable().optional(),
});

export const LocationStatusUpdateSchema = z.object({
  id: z.uuid(),
  isActive: z.boolean(),
});

export const LocationEpicorLoginFormSchema = z.object({
  username: z.string().min(1),
  password: z.string().min(1),
});

export const UpdateEpicorCredentialsRequestSchema =
  LocationEpicorLoginSchema.extend({
    serviceLocationId: z.uuid(),
  });

export const LocationSummarySearchResponseFeSchema = z
  .object({
    results: LocationListSchema,
  })
  .extend(PagedResultSchema.shape);

export const LocationLookupResultSchema = z.object({
  code: z.string(),
  name: z.string(),
  distance: z.number(),
  serviceLocationId: z.uuid(),
});

export const LocationLookupRequestSchema = z.object({
  searchText: z.coerce.string<string>().min(1),
});

export const LocationLookupResponseSchema = z.object({
  results: z.array(LocationLookupResultSchema),
});

export const LocationFilterSchema = z.object({
  name: z.coerce.string<string>().optional(),
});

export const LocationSearchRequestSchema = LocationFilterSchema.extend(
  PagedRequestSchema.shape
).extend({
  listOfIds: z.string().optional(),
  userId: z.string().optional(),
  code: z.string().optional(),
  type: z.string().optional(),
  assignedUserId: z.string().optional(),
  justMyLocations: z.boolean().optional(),
  loadLinkedCategories: z.boolean().optional(),
});

export const LocationTimezonesRequestSchema = z.object({
  countryCode: z.string(),
});

export const LocationTimezonesResponseSchema = z.array(
  z.object({
    name: z.string(),
    zoneId: z.string(),
    baseUtcOffset: z.string(),
    displayName: z.string(),
  })
);

export const MyLocationsSchema = z.array(LocationSchema);

export const LocationSearchFormSchema = LocationFilterSchema.extend(
  PagedRequestSchema.shape
);

export const ServiceAvailabilityBulkActionsEnum = z.enum([
  'enableAll',
  'enableAM',
  'enableAllDay',
  'enablePM',
  'disableAll',
  'disableAM',
  'disablePM',
  'disableAllDay',
]);

export type CreateLocationCategoryModel = z.infer<
  typeof CreateLocationCategorySchema
>;
export type LocationCategoryRequestModel = z.infer<
  typeof LocationCategoryRequestSchema
>;
export type LocationCategoryModel = z.infer<typeof LocationCategorySchema>;
export type LocationCategoryResponseModel = z.infer<
  typeof LocationCategoryResponseSchema
>;
export type LocationCategoryLocationsModel = z.infer<
  typeof LocationCategoryLocationsSchema
>;
export const AssignedTechniciansRequestSchema = z
  .object({
    serviceLocationId: z.uuid(),
    scheduleStart: z.coerce.date(),
    scheduleEnd: z.coerce.date(),
    orderBy: z.enum(['firstName', 'lastName', 'userName']).optional(),
  })
  .extend(PagedRequestSchema.omit({ orderBy: true }).shape);

export const AssignedTechnicianScheduleSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
  scheduleType: z.string(),
});

export const AssignedTechnicianSchema = z.object({
  id: z.string(),
  username: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  isScheduled: z.boolean(),
  schedules: z.array(AssignedTechnicianScheduleSchema),
});

export const AssignedTechniciansResponseSchema = z
  .object({
    results: z.array(AssignedTechnicianSchema),
  })
  .extend(PagedResultSchema.shape);

export type AssignedTechnicianModel = z.infer<typeof AssignedTechnicianSchema>;
export type AssignedTechnicianScheduleModel = z.infer<
  typeof AssignedTechnicianScheduleSchema
>;
export type AssignedTechniciansResponseModel = z.infer<
  typeof AssignedTechniciansResponseSchema
>;
export type AssignedTechniciansRequestModel = z.infer<
  typeof AssignedTechniciansRequestSchema
>;

export type LocationSearchRequestModel = z.infer<
  typeof LocationSearchRequestSchema
>;

export type LocationLinkedAssetResponseModel = z.infer<
  typeof LocationLinkedAssetResponseSchema
>;

export type LocationLinkedAssetResultModel = z.infer<
  typeof LocationLinkedAssetResultSchema
>;

export type LocationSearchFormModel = z.infer<typeof LocationSearchFormSchema>;

export type LocationLookupRequestModel = z.infer<
  typeof LocationLookupRequestSchema
>;
export type LocationLookupResponseModel = z.infer<
  typeof LocationLookupResponseSchema
>;

export type LocationCreateEditModel = z.infer<typeof LocationCreateEditSchema>;
export type LocationStatusUpdateModel = z.infer<
  typeof LocationStatusUpdateSchema
>;
export type LocationEpicorLoginModel = z.infer<
  typeof LocationEpicorLoginSchema
>;

export type UpdateEpicorCredentialsModel = z.infer<
  typeof UpdateEpicorCredentialsRequestSchema
>;

export type LocationEpicorLoginFormModel = z.infer<
  typeof LocationEpicorLoginFormSchema
>;

export type AddressCreateEditModel = z.infer<typeof AddressCreateEditSchema>;

export type LocationSummarySearchResponseFeModel = z.infer<
  typeof LocationSummarySearchResponseFeSchema
>;
export type LocationLinkedAssetModel = z.infer<typeof LocationLinkedAsset>;
export type GeolocationCreateEditModel = z.infer<
  typeof GeolocationCreateEditSchema
>;
export type LocationModel = z.infer<typeof LocationSchema>;
export type LocationSearchResultModel = z.infer<
  typeof LocationSearchResultsSchema
>;
export type LocationCustomConfigurationListModel = z.infer<
  typeof LocationCustomConfigurationListSchema
>;

export type MyLocationsModel = z.infer<typeof MyLocationsSchema>;

export const ServiceLocationIdSchema = z.object({
  serviceLocationId: z.uuid(),
});

export type ServiceLocationConfigurationModel = z.infer<
  typeof ServiceLocationConfigurationSchema
>;
export type ServiceLocationConfigurationV2Model = z.infer<
  typeof ServiceLocationConfigurationSchemaV2
>;

export type ServiceLocationConfigurationUpdateRequestModel = z.infer<
  typeof ServiceLocationConfigurationUpdateRequestSchema
>;

export type ServiceLocationConfigurationV2UpdateRequestModel = z.infer<
  typeof ServiceLocationConfigurationV2UpdateRequestSchema
>;

export type ServiceLocationConfigurationV2CreateRequestModel = z.infer<
  typeof ServiceLocationConfigurationV2CreateRequestSchema
>;

export type ServiceLocationConfigurationV2GetRequestModel = z.infer<
  typeof ServiceLocationConfigurationV2GetRequestSchema
>;

export type ServiceLocationConfigurationV2GetResponseModel = z.infer<
  typeof ServiceLocationConfigurationV2GetResponseSchema
>;

export type ServiceLocationConfigurationV2DeleteRequestModel = z.infer<
  typeof ServiceLocationConfigurationV2DeleteRequestSchema
>;

export type ServiceLocationConfigurationGetResponseModel = z.infer<
  typeof ServiceLocationConfigurationGetResponseSchema
>;

export type LocationIntegrationFindFERequestSchemaModel = z.infer<
  typeof LocationIntegrationFindFERequestSchema
>;

export type LocationTimezonesRequestModel = z.infer<
  typeof LocationTimezonesRequestSchema
>;

export type LocationTimezonesReseponseModel = z.infer<
  typeof LocationTimezonesResponseSchema
>;
